import React, { useMemo, useState } from 'react';
import Header from "../Header";
import { useDashboard, useDashboardData } from "../../contexts/DashboardContext";
import type { Tab } from "@/services/DashboardService";
import KeyMetricsTab from '@/components/tabs/KeyMetricsTab';
import AgentsTab from '@/components/tabs/AgentsTab';
import { DASHBOARD_TABS } from '@/services/DashboardService';
import CallsTab from '@/components/tabs/CallsTab';
import { useVersion } from '@/contexts/VersionContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAppData } from '@/contexts/AppDataContext';
import { CallQueueType } from '@/constants/queue';
import ChatWidget from '@/components/ai/ChatWidget';

const GeneralLayout: React.FC = () => {
  const [showAddWidget, setShowAddWidget] = useState(false);
  const { currentWidgetSettings, closeWidgetSettings, theme } = useDashboard();
  const { current } = useDashboardData();
  const { fetchLatestVersionAndApply } = useVersion();
  const { gromToken } = useAuth();
  const { queues, uniqueAgents } = useAppData();

  const chatDashboardContext = useMemo(() => {
    const queueIds = queues.map(q => q.microsoftId).filter(Boolean);
    const agentIds = uniqueAgents
      .filter(a => a.queueType === CallQueueType.CALL_QUEUE)
      .map(a => a.microsoftId)
      .filter(Boolean);
    const directAgentIds = uniqueAgents
      .filter(a => a.queueType === CallQueueType.DIRECT_AGENT_GROUP)
      .map(a => a.microsoftId)
      .filter(Boolean);
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offsetInMinutes: new Date().getTimezoneOffset() * -1,
      queueIds,
      agentIds,
      directAgentIds,
      allQueuesSelected: true,
      allAgentsSelected: true,
      allDirectAgentsSelected: true,
    };
  }, [queues, uniqueAgents]);

  // Find the first visible tab from current dashboard
  const firstTabValue = React.useMemo(() => {
    if (!current || !current.tabs) return "";
    const firstTab = current.tabs.find((tab: Tab) => !tab.hidden);
    return firstTab ? firstTab.name : "";
  }, [current]);

  const [activeTab, setActiveTab] = useState(firstTabValue);

  // Update activeTab if current changes
  React.useEffect(() => {
    if (firstTabValue) setActiveTab(firstTabValue);
  }, [firstTabValue]);

  const handleOpenAddWidget = () => {
    if (currentWidgetSettings) {
      closeWidgetSettings();
    }
    setShowAddWidget(true);
  };

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    // Auto scroll to top when switching tabs
    const container = document.querySelector('.overflow-auto');
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Check for new version when switching tabs
    fetchLatestVersionAndApply();
  };

  return (
    <div className={`flex h-screen ${theme === 'dark' ? 'bg-[#18181c]' : 'bg-gray-100'}`}>
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          title="General Dashboard"
          onAddWidget={handleOpenAddWidget}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <div className={`relative w-full h-full py-6 px-4 ${theme === 'dark' ? 'bg-[#0C3451]' : 'bg-white'} overflow-auto`}>
          {activeTab === DASHBOARD_TABS.KEY_METRICS && (
            <KeyMetricsTab isGeneralMode={true} />
          )}
          {activeTab === DASHBOARD_TABS.CALL_OVERVIEW && (
            <CallsTab isGeneralMode={true} />
          )}
          {activeTab === DASHBOARD_TABS.AGENT_PERFORMANCE && (
            <AgentsTab isGeneralMode={true} />
          )}
        </div>
      </div>
      {gromToken && (
        <ChatWidget
          bearerToken={gromToken}
          locale="en"
          timezone={chatDashboardContext.timezone}
          dashboardContext={chatDashboardContext}
        />
      )}
    </div>
  );
};

export default GeneralLayout;
